import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import { PersonAdd, Business, CheckCircle, Warning } from "@mui/icons-material";
import { useCreateDoctorWithClinic } from "../../../stores/Doctor/doctor.mutations";
import { useApi } from "../../../api/ApiProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DoctorMatch, ClinicMatch } from "./useExtractionMatching";
import { CreateDoctorClinicDto } from "../../../api-client";

// ============================================================================
// Types
// ============================================================================

interface DoctorClinicMatchDialogProps {
  open: boolean;
  onClose: () => void;
  doctorMatch: DoctorMatch;
  clinicMatch: ClinicMatch;
}

// ============================================================================
// Parse helpers
// ============================================================================

/** Best-effort split of "Dr. med. Max Mustermann" → { firstNames, lastName } */
function parseDoctorName(name: string | null): { firstNames: string; lastName: string } {
  if (!name) return { firstNames: "", lastName: "" };
  // Remove common titles
  const cleaned = name.replace(/^(Dr\.?\s*(med\.?)?|Prof\.?\s*)/i, "").trim();
  const parts = cleaned.split(/\s+/);
  if (parts.length <= 1) return { firstNames: "", lastName: cleaned };
  const lastName = parts.pop()!;
  return { firstNames: parts.join(" "), lastName };
}

/** Parse "Street 1, 12345 City" or multi-line into contact fields */
function parseAddress(address: string | null): {
  addressLine1: string;
  zip: string;
  city: string;
} {
  if (!address) return { addressLine1: "", zip: "", city: "" };
  const lines = address.split(/[,\n]/).map((l) => l.trim()).filter(Boolean);
  if (lines.length >= 2) {
    const street = lines[0];
    const zipCity = lines[lines.length - 1].match(/^(\d{5})\s+(.+)/);
    if (zipCity) return { addressLine1: street, zip: zipCity[1], city: zipCity[2] };
    return { addressLine1: street, zip: "", city: lines[lines.length - 1] };
  }
  const single = address.match(/^(.+?)\s+(\d{5})\s+(.+)$/);
  if (single) return { addressLine1: single[1], zip: single[2], city: single[3] };
  return { addressLine1: address, zip: "", city: "" };
}

/** Parse "Tel: 030/123456 Fax: 030/654321" */
function parseContact(contact: string | null): { phone1: string; fax: string } {
  if (!contact) return { phone1: "", fax: "" };
  const phoneMatch = contact.match(/(?:Tel\.?|Telefon):?\s*([\d\s/\-+()]+)/i);
  const faxMatch = contact.match(/Fax:?\s*([\d\s/\-+()]+)/i);
  return {
    phone1: phoneMatch?.[1]?.trim() || "",
    fax: faxMatch?.[1]?.trim() || contact.trim(),
  };
}

// ============================================================================
// Sub-component: existing data display
// ============================================================================

const ExistingRecord: React.FC<{
  label: string;
  id: string;
  contactInfo: any;
  status: "matched" | "partial";
  missingFields: string[];
  extraFields?: Record<string, string | null | undefined>;
}> = ({ label, id, contactInfo, status, missingFields, extraFields }) => {
  const ci = contactInfo || {};

  // Build list of present fields for display
  const presentEntries: { name: string; value: string }[] = [];
  if (ci.firstNames || ci.lastName) presentEntries.push({ name: "Name", value: [ci.firstNames, ci.lastName].filter(Boolean).join(" ") });
  if (ci.businessName) presentEntries.push({ name: "Praxisname", value: ci.businessName });
  if (ci.addressLine1) presentEntries.push({ name: "Adresse", value: [ci.addressLine1, ci.zip, ci.city].filter(Boolean).join(", ") });
  if (ci.phone1) presentEntries.push({ name: "Telefon", value: ci.phone1 });
  if (ci.fax) presentEntries.push({ name: "Fax", value: ci.fax });
  if (ci.email) presentEntries.push({ name: "E-Mail", value: ci.email });
  if (extraFields) {
    for (const [name, value] of Object.entries(extraFields)) {
      if (value) presentEntries.push({ name, value });
    }
  }

  return (
    <Box
      sx={{
        p: 1.5,
        border: "1px solid",
        borderColor: status === "matched" ? "success.main" : "warning.main",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        {status === "matched" ? (
          <CheckCircle color="success" fontSize="small" />
        ) : (
          <Warning color="warning" fontSize="small" />
        )}
        <Typography variant="subtitle2">
          {label}: {id}
        </Typography>
        <Chip
          size="small"
          label={status === "matched" ? "Vollständig" : "Unvollständig"}
          color={status === "matched" ? "success" : "warning"}
          variant="outlined"
        />
      </Box>

      {/* Show all present data */}
      {presentEntries.length > 0 ? (
        <Box sx={{ mt: 0.5 }}>
          {presentEntries.map(({ name, value }) => (
            <Typography key={name} variant="caption" color="text.secondary" component="div">
              <strong>{name}:</strong> {value}
            </Typography>
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Keine Daten hinterlegt
        </Typography>
      )}

      {missingFields.length > 0 && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: "block" }}>
          Fehlend: {missingFields.join(", ")}
        </Typography>
      )}
    </Box>
  );
};

// ============================================================================
// Component
// ============================================================================

const DoctorClinicMatchDialog: React.FC<DoctorClinicMatchDialogProps> = ({
  open,
  onClose,
  doctorMatch,
  clinicMatch,
}) => {
  const createDoctorWithClinic = useCreateDoctorWithClinic();
  const { doctors: doctorsApi } = useApi();
  const queryClient = useQueryClient();

  // Update clinic mutation (inline since there's no dedicated hook)
  const updateClinic = useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: any }) => {
      return await doctorsApi.doctorControllerUpdateClinic(id, dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["extraction-clinic-match"] });
      queryClient.invalidateQueries({ queryKey: ["doctorClinics"] });
    },
  });

  const doctorName = parseDoctorName(doctorMatch.extracted.doctorName);
  const clinicAddr = parseAddress(clinicMatch.extracted.practiceAddress);
  const clinicContact = parseContact(clinicMatch.extracted.practiceContact);

  // Doctor form
  const [doctorForm, setDoctorForm] = useState({
    firstNames: doctorName.firstNames,
    lastName: doctorName.lastName,
  });

  // Clinic form
  // Clinic form — use doctorSpecialization as fallback for Praxisname
  const specializationFallback = clinicMatch.extracted.doctorSpecialization || "";

  const [clinicForm, setClinicForm] = useState({
    businessName: specializationFallback,
    addressLine1: clinicAddr.addressLine1,
    zip: clinicAddr.zip,
    city: clinicAddr.city,
    phone1: clinicContact.phone1,
    fax: clinicContact.fax,
  });

  // Re-sync when dialog opens
  React.useEffect(() => {
    if (open) {
      const dn = parseDoctorName(doctorMatch.extracted.doctorName);
      const ca = parseAddress(clinicMatch.extracted.practiceAddress);
      const cc = parseContact(clinicMatch.extracted.practiceContact);
      setDoctorForm({ firstNames: dn.firstNames, lastName: dn.lastName });
      setClinicForm({
        businessName: clinicMatch.extracted.doctorSpecialization || "",
        addressLine1: ca.addressLine1,
        zip: ca.zip,
        city: ca.city,
        phone1: cc.phone1,
        fax: cc.fax,
      });
    }
  }, [open, doctorMatch.extracted, clinicMatch.extracted]);

  const doctorNeedsAction = doctorMatch.status === "new" || doctorMatch.status === "partial";
  const clinicNeedsAction = clinicMatch.status === "new" || clinicMatch.status === "partial";
  const bothNew = doctorMatch.status === "new" && clinicMatch.status === "new";
  const saving = createDoctorWithClinic.isPending || updateClinic.isPending;

  const handleSave = () => {
    // Case 1: Both new → create doctor with clinic
    if (bothNew) {
      const dto: CreateDoctorClinicDto = {
        id: doctorMatch.extracted.doctorNumber!,
        contactInfo: {
          firstNames: doctorForm.firstNames,
          lastName: doctorForm.lastName,
        },
        clinics: clinicMatch.extracted.practiceNumber
          ? [
              {
                id: clinicMatch.extracted.practiceNumber,
                contactInfo: {
                  businessName: clinicForm.businessName,
                  addressLine1: clinicForm.addressLine1,
                  city: clinicForm.city,
                  zip: clinicForm.zip,
                  phone1: clinicForm.phone1,
                  fax: clinicForm.fax,
                },
                doctorNumbers: [doctorMatch.extracted.doctorNumber!],
              },
            ]
          : [],
      };
      createDoctorWithClinic.mutate(dto, { onSuccess: () => onClose() });
      return;
    }

    // Case 2: Doctor new, clinic exists
    if (doctorMatch.status === "new" && clinicMatch.status !== "new") {
      const dto: CreateDoctorClinicDto = {
        id: doctorMatch.extracted.doctorNumber!,
        contactInfo: {
          firstNames: doctorForm.firstNames,
          lastName: doctorForm.lastName,
        },
        clinics: clinicMatch.extracted.practiceNumber
          ? [{ id: clinicMatch.extracted.practiceNumber, contactInfo: {}, doctorNumbers: [] }]
          : [],
      };
      createDoctorWithClinic.mutate(dto, { onSuccess: () => onClose() });
      return;
    }

    // Case 3: Clinic needs update (partial)
    if (clinicMatch.status === "partial" && clinicMatch.extracted.practiceNumber) {
      const existingCi = clinicMatch.existingClinic?.contactInfo || {};
      updateClinic.mutate(
        {
          id: clinicMatch.extracted.practiceNumber,
          dto: {
            contactInfo: {
              ...existingCi,
              addressLine1: existingCi.addressLine1 || clinicForm.addressLine1,
              city: existingCi.city || clinicForm.city,
              zip: existingCi.zip || clinicForm.zip,
              phone1: existingCi.phone1 || clinicForm.phone1,
              fax: existingCi.fax || clinicForm.fax,
              businessName: existingCi.businessName || clinicForm.businessName,
            },
          },
        },
        { onSuccess: () => onClose() }
      );
    }
  };

  const allMatched = doctorMatch.status === "matched" && clinicMatch.status === "matched";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Arzt / Praxis Abgleich</DialogTitle>

      <DialogContent dividers>
        {/* ── Doctor section ───────────────────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <PersonAdd color="action" />
          <Typography variant="subtitle1" fontWeight={600}>
            Arzt (LANR: {doctorMatch.extracted.doctorNumber || "—"})
          </Typography>
        </Box>

        {doctorMatch.status === "matched" || doctorMatch.status === "partial" ? (
          <ExistingRecord
            label="Arzt"
            id={doctorMatch.extracted.doctorNumber!}
            contactInfo={doctorMatch.existingDoctor?.contactInfo}
            status={doctorMatch.status}
            missingFields={doctorMatch.missingFields}
          />
        ) : doctorMatch.status === "new" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Arzt nicht im System — wird neu angelegt
            </Alert>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                label="Vorname"
                value={doctorForm.firstNames}
                onChange={(e) => setDoctorForm((p) => ({ ...p, firstNames: e.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                label="Nachname"
                value={doctorForm.lastName}
                onChange={(e) => setDoctorForm((p) => ({ ...p, lastName: e.target.value }))}
                fullWidth
              />
            </Box>
          </Box>
        ) : null}

        <Divider sx={{ my: 2 }} />

        {/* ── Clinic section ──────────────────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <Business color="action" />
          <Typography variant="subtitle1" fontWeight={600}>
            Praxis (BSNR: {clinicMatch.extracted.practiceNumber || "—"})
          </Typography>
        </Box>

        {clinicMatch.status === "matched" || clinicMatch.status === "partial" ? (
          <ExistingRecord
            label="Praxis"
            id={clinicMatch.extracted.practiceNumber!}
            contactInfo={clinicMatch.existingClinic?.contactInfo}
            status={clinicMatch.status}
            missingFields={clinicMatch.missingFields}
          />
        ) : clinicMatch.status === "new" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Praxis nicht im System — wird neu angelegt
            </Alert>
            <TextField
              size="small"
              label="Praxisname"
              value={clinicForm.businessName}
              onChange={(e) => setClinicForm((p) => ({ ...p, businessName: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label="Straße & Hausnr."
              value={clinicForm.addressLine1}
              onChange={(e) => setClinicForm((p) => ({ ...p, addressLine1: e.target.value }))}
              fullWidth
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                label="PLZ"
                value={clinicForm.zip}
                onChange={(e) => setClinicForm((p) => ({ ...p, zip: e.target.value }))}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                label="Ort"
                value={clinicForm.city}
                onChange={(e) => setClinicForm((p) => ({ ...p, city: e.target.value }))}
                fullWidth
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                label="Telefon"
                value={clinicForm.phone1}
                onChange={(e) => setClinicForm((p) => ({ ...p, phone1: e.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                label="Fax"
                value={clinicForm.fax}
                onChange={(e) => setClinicForm((p) => ({ ...p, fax: e.target.value }))}
                fullWidth
              />
            </Box>
          </Box>
        ) : null}

        {/* If clinic is partial, show editable fields for missing data */}
        {clinicMatch.status === "partial" && clinicMatch.missingFields.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
            <Typography variant="subtitle2">Fehlende Daten ergänzen:</Typography>
            {clinicMatch.missingFields.includes("Praxisname") && (
              <TextField
                size="small"
                label="Praxisname"
                value={clinicForm.businessName}
                onChange={(e) => setClinicForm((p) => ({ ...p, businessName: e.target.value }))}
                fullWidth
              />
            )}
            {clinicMatch.missingFields.includes("Adresse") && (
              <>
                <TextField
                  size="small"
                  label="Straße & Hausnr."
                  value={clinicForm.addressLine1}
                  onChange={(e) => setClinicForm((p) => ({ ...p, addressLine1: e.target.value }))}
                  fullWidth
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    size="small"
                    label="PLZ"
                    value={clinicForm.zip}
                    onChange={(e) => setClinicForm((p) => ({ ...p, zip: e.target.value }))}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    size="small"
                    label="Ort"
                    value={clinicForm.city}
                    onChange={(e) => setClinicForm((p) => ({ ...p, city: e.target.value }))}
                    fullWidth
                  />
                </Box>
              </>
            )}
            {clinicMatch.missingFields.includes("Kontakt") && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  label="Telefon"
                  value={clinicForm.phone1}
                  onChange={(e) => setClinicForm((p) => ({ ...p, phone1: e.target.value }))}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Fax"
                  value={clinicForm.fax}
                  onChange={(e) => setClinicForm((p) => ({ ...p, fax: e.target.value }))}
                  fullWidth
                />
              </Box>
            )}
          </Box>
        )}

        {allMatched && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Arzt und Praxis sind bereits vollständig im System hinterlegt.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {allMatched ? "Schließen" : "Abbrechen"}
        </Button>
        {!allMatched && (doctorNeedsAction || clinicNeedsAction) && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {saving ? "Wird gespeichert…" : "Speichern"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DoctorClinicMatchDialog;
